import { Tooltip, TooltipProps } from '@mui/material';
import { ReactElement, useState } from 'react';

const popperModifiers = [
    {
        name: 'offset' as const,
        options: { offset: [-9, -2] },
    },
];

const tooltipSx = (theme: {
    palette: { divider: string; background: { paper: string } };
}) => ({
    py: 2,
    px: 2,
    borderRadius: 0,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
});

interface PropertiesTooltipProps
    extends Omit<TooltipProps, 'enterDelay' | 'leaveDelay'> {
    children: ReactElement;
    /**
     * An element to hang the tooltip from instead of the child, its edge
     * flush with the tooltip's: no offset and no gap.
     */
    anchorEl?: HTMLElement | null;
}

/**
 * The bordered properties tooltip. Opens on hover and focus by itself; given
 * an `open`, it shows exactly when the caller says and reports Escape and
 * clicks inside through `onClose`. Given an `anchorEl`, it hangs flush from
 * that element while its child still opens it.
 */
export default function PropertiesTooltip({
    children,
    placement = 'right-start',
    open: controlledOpen,
    onOpen,
    onClose,
    anchorEl,
    ...rest
}: PropertiesTooltipProps) {
    const flush = anchorEl != null;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const open = controlledOpen ?? uncontrolledOpen;

    return (
        <Tooltip
            open={open}
            onOpen={(event) => {
                setUncontrolledOpen(true);
                onOpen?.(event);
            }}
            onClose={(event) => {
                setUncontrolledOpen(false);
                onClose?.(event);
            }}
            placement={placement}
            enterDelay={500}
            leaveDelay={200}
            slotProps={{
                popper: flush ? { anchorEl } : { modifiers: popperModifiers },
                tooltip: {
                    sx: (theme) => ({
                        ...tooltipSx(theme),
                        // The placement margin would part it from its anchor.
                        ...(flush ? { margin: '0 !important' } : {}),
                    }),
                    onClickCapture: (event) => {
                        setUncontrolledOpen(false);
                        onClose?.(event);
                    },
                },
            }}
            {...rest}
        >
            {children}
        </Tooltip>
    );
}
